import React, { useEffect, useState } from "react";
import { Twemoji } from "@svgmoji/twemoji";
import data from "svgmoji/emoji.json";

const twemoji = new Twemoji({ data, type: "all" });

const twemojiMap = new Map();

const defaultProps = {
  rotationAngles: [45, 70],
  emojiSize: 23,
  emojiGapSize: 14,
};
export const useEmojiCanvasImages = ({
  emojis: _emojis,
  emojiSize = defaultProps.emojiSize,
  emojiGapSize = defaultProps.emojiGapSize,
  color1,
  // color2,
  width,
  height,
  rotationAngles = defaultProps.rotationAngles,
  shouldCache = true,
}: {
  emojis: string[];
  emojiSize?: number;
  emojiGapSize?: number;
  color1: string;
  // color2: string;
  width: number;
  height: number;
  rotationAngles?: number[];
  animationInterval?: number;
  shouldCache?: boolean;
}) => {
  // stop re-rendering from emojis
  const emojis = React.useMemo(() => {
    return _emojis;
  }, [_emojis]);

  const [imgMap, setImgMap] = useState<{ [key: string]: string }>({});

  const isReady = imgMap && Object.keys(imgMap).length >= rotationAngles.length;

  const canvasRef = React.useRef(new OffscreenCanvas(width, height));

  useEffect(() => {
    // preload the images from the img map urls
    if (!isReady) return;
    Object.values(imgMap).forEach((url) => {
      const img = new Image();
      img.src = url;
    });
  }, [isReady, imgMap]);

  useEffect(() => {
    async function draw() {
      const canvas = canvasRef.current;

      for (const rotationAngle of rotationAngles) {
        if (!!imgMap?.[rotationAngle.toString()] && shouldCache) return;
        // const canvas = canvasRef.current;
        const context = canvas.getContext("2d");

        // Get the device pixel ratio, falling back to 1.
        const dpr = window.devicePixelRatio || 1;

        // Set canvas dimensions scaled for high DPI
        canvas.width = width * dpr;
        canvas.height = height * dpr;

        // Normalize the CSS size of the canvas, so it's the same as the original width/height
        // canvas.style.width = `${width}px`;
        // canvas.style.height = `${height}px`;

        // Scale the drawing context to ensure correct drawing on the scaled canvas
        context.scale(dpr, dpr);

        // Create gradient background
        // const gradient = context.createLinearGradient(0, 0, 0, height);
        // Create circular gradient
        // Calculate the diagonal length of the canvas
        const diagonal = Math.sqrt(width * width + height * height) * 1.5;

        // Create circular gradient with the diagonal as the radius
        const gradient = context.createRadialGradient(
          width / 2,
          height / 2,
          0,
          width / 2,
          height / 2,
          diagonal
        );

        gradient.addColorStop(0, color1);
        // gradient.addColorStop(1, color2);
        context.fillStyle = gradient;
        context.fillRect(0, 0, width, height);
        const gapSize = emojiGapSize; // Gap between emojis

        // Draw diamond pattern of emojis
        // const emojiSize = 23; // Size of each emoji
        const rows = Math.ceil(height / (emojiSize + gapSize) + 1);
        const cols = Math.ceil(width / (emojiSize + gapSize) + 1);

        // const verticalOffset = (emojiSize + gapSize) / 2;
        // const verticalOffset = -14;
        const verticalOffset = 0;
        // const horizontalOffset = -10;
        const horizontalOffset = 0;

        for (const row of Array(rows).keys()) {
          // for (let row = 0; row < rows; row++) {

          for (const col of Array(cols).keys()) {
            // for (let col = 0; col < cols; col++) {
            const x =
              col * (emojiSize + gapSize) + emojiSize / 2 + horizontalOffset;
            const y =
              row * (emojiSize + gapSize) + emojiSize / 2 + verticalOffset;
            const emojiIndex = (row + col) % emojis.length;
            const emoji = emojis[emojiIndex];

            context.save(); // Save the current state

            context.translate(x, y); // Move to the (x,y) position
            const currentRotationAngle =
              col % 2 ? -rotationAngle : rotationAngle; // Angle in degrees
            // const rotationAngle = -45; // Angle in degrees
            context.rotate((currentRotationAngle * Math.PI) / 180); // Rotate

            context.globalAlpha = 1;
            // context.globalAlpha = 0.7;
            context.font = `${emojiSize}px sans-serif`;
            context.textAlign = "center";
            context.textBaseline = "middle";
            // context.fillText(emoji, 0, 0); // Draw at the new origin

            // use twemoji to render the emoji

            let image = twemojiMap.get(twemoji.url(emoji));

            if (!image) {
              image = await loadImage(twemoji.url(emoji));
              twemojiMap.set(twemoji.url(emoji), image);
            }

            // const img = await loadImage("https://placehold.co/600x400");
            // context.drawImage(img, 0, 0);
            context.drawImage(
              image,
              -emojiSize / 2,
              -emojiSize / 2,
              emojiSize,
              emojiSize
            );

            context.restore(); // Restore to the original state
          }
        }
        canvas?.convertToBlob().then((blob) => {
          // this is async so it needs to use the callback form of setImgMap
          setImgMap((imgMap) => ({
            ...imgMap,
            [rotationAngle.toString()]: URL.createObjectURL(blob),
          }));
        });
      }
    }
    draw();
  }, [
    emojis,
    color1,
    // color2,
    width,
    height,
    rotationAngles,
    shouldCache,
    emojiGapSize,
    emojiSize,
  ]);

  return { imgMap, isReady };
};

async function loadImage(url: string) {
  return new Promise((r) => {
    let i = new Image();
    i.crossOrigin = "Anonymous";
    i.onload = () => r(i);
    i.src = url;
  });
}
